import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * LOS CUATRO ESTADOS DE UN LISTADO (RF-087, D-83, RN-140 a RN-142, RNF-042).
 *
 * "SIN REGISTROS" ES UN RESULTADO, NO UNA ETAPA. Ningún listado puede afirmar que
 * no hay nada antes de tener la respuesta del servidor: el estado inicial es
 * CARGANDO, nunca VACIO (RN-141). Un fallo de red se muestra como ERROR con
 * Reintentar, jamás disfrazado de "no tienes registros".
 *
 * Los cuatro estados son EXCLUYENTES: nunca se muestran dos a la vez (RN-140).
 *
 * RN-142a / RNF-042: al recargar un listado YA MOSTRADO, los datos previos
 * permanecen visibles con indicación de actualización. La lista no se vacía y no
 * parpadea.
 */

export const ESTADO_DE_LISTADO = Object.freeze({
  CARGANDO: 'CARGANDO',
  CON_DATOS: 'CON_DATOS',
  VACIO: 'VACIO',
  ERROR: 'ERROR',
});

/**
 * @param consultar  función asíncrona que trae los datos. Debe ser estable
 *                   (useCallback), porque cambiarla dispara una consulta nueva:
 *                   es lo que hace que cambiar un filtro vuelva a CARGANDO
 *                   (RN-142b).
 * @param estaVacio  cómo se sabe que la respuesta correcta trae CERO registros.
 *                   Es propio de cada listado, así que lo decide quien lo usa.
 */
export function useConsultaDeListado(consultar, estaVacio) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const consultaVigente = useRef(0);

  const ejecutar = useCallback(async () => {
    const consulta = consultaVigente.current + 1;
    consultaVigente.current = consulta;

    // CARGANDO desde que se dispara la consulta hasta que llega respuesta o error,
    // sin estados intermedios (RNF-042). Los datos previos NO se borran aquí: se
    // conservan visibles mientras llega la respuesta nueva (RN-142a).
    setCargando(true);
    setError(null);
    try {
      const respuesta = await consultar();
      if (consultaVigente.current !== consulta) return; // llegó una respuesta vieja
      setDatos(respuesta);
    } catch (fallo) {
      if (consultaVigente.current !== consulta) return;
      // Un fallo NO se muestra como vacío: se muestra como error (RN-141).
      setError(fallo);
    } finally {
      if (consultaVigente.current === consulta) setCargando(false);
    }
  }, [consultar]);

  useEffect(() => { ejecutar(); }, [ejecutar]);

  const hayDatos = datos !== null;
  const vacio = hayDatos && estaVacio(datos);

  let estado;
  if (error) estado = ESTADO_DE_LISTADO.ERROR;
  else if (cargando && !hayDatos) estado = ESTADO_DE_LISTADO.CARGANDO;
  else if (vacio) estado = ESTADO_DE_LISTADO.VACIO;
  else if (hayDatos) estado = ESTADO_DE_LISTADO.CON_DATOS;
  else estado = ESTADO_DE_LISTADO.CARGANDO;

  return {
    estado,
    datos,
    error,
    // Recarga de un listado ya mostrado: la lista sigue a la vista y se avisa que
    // se está actualizando (RN-142a).
    actualizando: cargando && hayDatos,
    recargar: ejecutar,
    // Deja el listado como recién abierto. Se usa al cambiar de filtro cuando el
    // resultado anterior ya no corresponde (RN-142b).
    reiniciar: useCallback(() => { setDatos(null); setError(null); setCargando(true); }, []),
  };
}
